import React, { createContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import config from "../config";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [favourites, setFavourites] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load from localStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const storedToken = localStorage.getItem("token");
    if (storedUser && storedToken) {
      setUser(JSON.parse(storedUser));
      setToken(storedToken);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, []);

  // Fetch user profile (e.g., favourites)
  const fetchUserProfile = useCallback(
    async (userID) => {
      if (!token) return;
      try {
        const resp = await axios.get(
          `${config.apiBaseUrl}/api/user/${userID}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setFavourites(resp.data.Favourites || []);
      } catch (err) {
        console.error("Fetch profile error", err);
      }
    },
    [token]
  );

  // Email/password signup
  const signup = useCallback(
    async ({ email, password, firstName, lastName, role }) => {
      setLoading(true);
      try {
        const resp = await axios.post(`${config.apiBaseUrl}/api/auth/signup`, {
          email,
          password,
          firstName,
          lastName,
          role,
        });
        const {
          token: jwtToken,
          userID,
          email: userEmail,
          name,
          role: userRole,
        } = resp.data;
        const newUser = {
          userID,
          email: userEmail,
          firstName: name,
          role: userRole,
        };
        setUser(newUser);
        setToken(jwtToken);
        localStorage.setItem("user", JSON.stringify(newUser));
        localStorage.setItem("token", jwtToken);
        await fetchUserProfile(userID);
      } catch (error) {
        console.error("Sign-up error:", error.response?.data || error.message);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchUserProfile]
  );

  // Email/password login
  const login = useCallback(
    async ({ email, password }, redirectPath) => {
      setLoading(true);
      try {
        const resp = await axios.post(`${config.apiBaseUrl}/api/auth/login`, {
          email,
          password,
        });
        const {
          token: jwtToken,
          userID,
          email: userEmail,
          name,
          role,
        } = resp.data;
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        const newUser = { userID, email: userEmail, firstName: name, role };
        setUser(newUser);
        setToken(jwtToken);
        localStorage.setItem("user", JSON.stringify(newUser));
        localStorage.setItem("token", jwtToken);
        await fetchUserProfile(userID);
        return redirectPath;
      } catch (error) {
        console.error("Login error:", error.response?.data || error.message);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fetchUserProfile]
  );

  // Google OAuth login
  const loginWithGoogle = useCallback(
    async (idToken) => {
      setLoading(true);
      try {
        const resp = await axios.post(`${config.apiBaseUrl}/api/auth/google`, {
          idToken,
        });
        const {
          token: jwtToken,
          userID,
          email: userEmail,
          name,
          role,
        } = resp.data;
        localStorage.removeItem("user"); // Clear old user data
        localStorage.removeItem("token"); // Clear old token
        const newUser = {
          userID,
          email: userEmail,
          firstName: name,
          role,
          googleId: true, // Mark as Google user
        };
        setUser(newUser);
        setToken(jwtToken);
        localStorage.setItem("user", JSON.stringify(newUser));
        localStorage.setItem("token", jwtToken);
        await fetchUserProfile(userID);
        return { token: jwtToken, userID };
      } catch (err) {
        console.error("Google login failed", err);
        throw new Error("Google login failed");
      } finally {
        setLoading(false);
      }
    },
    [fetchUserProfile]
  );

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    setFavourites([]);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        favourites,
        setFavourites,
        loading,
        signup,
        login,
        loginWithGoogle,
        logout,
        fetchUserProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
